import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import React, { useState } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import axiosInstance from "../../axios/axios";
import { toast } from "react-toastify";
import { useAuth, useEncryptService, useTokens } from "../../shared/hooks";
import { END_POINTS } from "../../shared";
import { useNavigate } from "react-router-dom";
import { CONSTANT } from "../../shared/constants/constant-data";

const passwordValidation = yup
  .string()
  .required(CONSTANT.AUTHENTICATION.SIGN_IN_PASSWORD_REQUIRED)
  .min(12, CONSTANT.AUTHENTICATION.AUTH_PASSWORD_MIN_LENGTH)
  .max(20, CONSTANT.AUTHENTICATION.AUTH_PASSWORD_MAX_LENGTH)
  .matches(/^\S*$/, CONSTANT.AUTHENTICATION.AUTH_PASSWORD_NO_SPACE)
  .matches(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^_\-+=(){}\[\].,:~`|\\;"'<>/])[A-Za-z\d@$!%*?&#^_\-+=(){}\[\].,:~`|\\;"'<>/]{12,20}$/,
    CONSTANT.AUTHENTICATION.AUTH_PASSWORD_FORMAT
  );

const confirmPasswordValidation = yup
  .string()
  .required(CONSTANT.AUTHENTICATION.CONFIRM_PASSWORD_REQUIRED)
  .min(12, CONSTANT.AUTHENTICATION.AUTH_PASSWORD_MIN_LENGTH)
  .max(20, CONSTANT.AUTHENTICATION.AUTH_PASSWORD_MAX_LENGTH)
  .matches(/^\S*$/, CONSTANT.AUTHENTICATION.AUTH_PASSWORD_NO_SPACE)
  .matches(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^_\-+=(){}\[\].,:~`|\\;"'<>/])[A-Za-z\d@$!%*?&#^_\-+=(){}\[\].,:~`|\\;"'<>/]{12,20}$/,
    CONSTANT.AUTHENTICATION.AUTH_PASSWORD_FORMAT
  );

const validationSchema = yup.object().shape({
  oldpassword: yup.string().required("Old Password is Required"),
  newpassword: passwordValidation,
  confirmpassword: confirmPasswordValidation,
});

const ChangePassword = ({
  isChangePasswordClicked,
  setIsChangePasswordClicked,
}: any) => {
  const [showOldPassword, setShowOldPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = React.useState(false);
  const [loading, setLoading] = useState(false);

  const { doEncryptLogin } = useEncryptService();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { clearTokens } = useTokens();

  const lastErrorMessageRef = React.useRef<string | null>(null);
  const toastTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const toastDuration = 5000;

  const formik = useFormik({
    initialValues: {
      oldpassword: "",
      newpassword: "",
      confirmpassword: "",
    },
    validationSchema,
    onSubmit: async (data) => {
      if (!isButtonDisabled) {
        setLoading(true);
        setIsButtonDisabled(true);
        try {
          const req_payload = {
            new_password: doEncryptLogin(data.newpassword),
            new_password1: doEncryptLogin(data.confirmpassword),
            old_password: doEncryptLogin(data.oldpassword),
          };
          const response = await axiosInstance.post(
            END_POINTS.CHANGE_PASSWORD,
            req_payload
          );

          toast(response.data.message, { type: "success" });
          if (response.data.status == 200) {
            await logout();
            clearTokens();
            navigate("/login");
          }

          console.log("Password changed successfully:", response.data);
        } catch (error: any) {
          console.error("Error changing password:", error);

          const errorMessage =
            error?.response?.data?.message || "Something went wrong";

          // Only show toast if error message is different or previous toast duration has ended
          if (
            lastErrorMessageRef.current !== errorMessage ||
            !toastTimeoutRef.current
          ) {
            toast(errorMessage, { type: "error" });
            lastErrorMessageRef.current = errorMessage;
            if (toastTimeoutRef.current) {
              clearTimeout(toastTimeoutRef.current);
            }
            toastTimeoutRef.current = setTimeout(() => {
              toastTimeoutRef.current = null;
              lastErrorMessageRef.current = null;
            }, toastDuration);
          }
          setIsButtonDisabled(false);
        } finally {
          setIsButtonDisabled(false);
          setLoading(false);
        }
      }
    },
  });

  const isPasswordMismatch =
    Boolean(formik.values.confirmpassword) &&
    formik.values.newpassword !== formik.values.confirmpassword;

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    borderRadius: 2,
    p: 2,
  };

  return (
    <div>
      <Modal
        open={isChangePasswordClicked}
        onClose={setIsChangePasswordClicked}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <Typography
              id="modal-modal-title"
              variant="body2"
              sx={{ fontWeight: "bold", fontSize: "18px" }}
            >
              Change Password
            </Typography>
            <CloseIcon
              style={{ cursor: "pointer" }}
              onClick={() => setIsChangePasswordClicked(false)}
            />
          </div>
          <Typography
            variant="body2"
            sx={{ fontSize: "14px", fontWeight: 400, mb: 3 }}
          >
            Enter your new password and confirm
          </Typography>

          <form style={{ flexGrow: 1 }} onSubmit={formik.handleSubmit}>
            <TextField
              id="oldpassword"
              label="Old Password"
              variant="outlined"
              required
              fullWidth
              name="oldpassword"
              type={showOldPassword ? "text" : "password"}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.oldpassword}
              error={
                formik.touched.oldpassword && Boolean(formik.errors.oldpassword)
              }
              helperText={
                formik.touched.oldpassword && formik.errors.oldpassword
              }
              sx={{ mb: 4 }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        sx={{
                          backgroundColor: "transparent",
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                          justifyContent: "end",
                          "&:focus": { outline: "none" },
                        }}
                      >
                        {showOldPassword ? (
                          <Visibility sx={{ color: "black" }} />
                        ) : (
                          <VisibilityOff sx={{ color: "black" }} />
                        )}
                      </Button>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              id="newpassword"
              label="New Password"
              variant="outlined"
              required
              fullWidth
              name="newpassword"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.newpassword}
              error={
                formik.touched.newpassword && Boolean(formik.errors.newpassword)
              }
              helperText={
                formik.touched.newpassword && formik.errors.newpassword
              }
              sx={{ mb: 4 }}
              type={showNewPassword ? "text" : "password"}
              inputProps={{ maxLength: 20 }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        sx={{
                          backgroundColor: "transparent",
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                          justifyContent: "end",
                          "&:focus": { outline: "none" },
                        }}
                      >
                        {showNewPassword ? (
                          <Visibility sx={{ color: "black" }} />
                        ) : (
                          <VisibilityOff sx={{ color: "black" }} />
                        )}
                      </Button>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              id="confirmpassword"
              label="Confirm Password"
              variant="outlined"
              required
              fullWidth
              name="confirmpassword"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.confirmpassword}
              error={
                (formik.touched.confirmpassword &&
                  Boolean(formik.errors.confirmpassword)) ||
                isPasswordMismatch
              }
              helperText={
                formik.touched.confirmpassword &&
                formik.errors.confirmpassword
              }
              sx={{ mb: 4 }}
              type={showConfirmPassword ? "text" : "password"}
              inputProps={{ maxLength: 20 }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        sx={{
                          backgroundColor: "transparent",
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                          justifyContent: "end",
                          "&:focus": { outline: "none" },
                        }}
                      >
                        {showConfirmPassword ? (
                          <Visibility sx={{ color: "black" }} />
                        ) : (
                          <VisibilityOff sx={{ color: "black" }} />
                        )}
                      </Button>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              variant="contained"
              fullWidth
              type="submit"
              sx={{ mb: 1 }}
              disabled={
                isButtonDisabled ||
                !formik.isValid ||
                !formik.dirty ||
                isPasswordMismatch
              }
              endIcon={
                loading ? <CircularProgress size={15} color="inherit" /> : null
              }
            >
              Change Password
            </Button>
          </form>
        </Box>
      </Modal>
    </div>
  );
};

export default ChangePassword;
